import { useEffect, useState } from "react";
import { Modal } from "@/components/ui/modal";

export type GuideTopic = "mint" | "event" | "automatic" | "trouble";

const TOPICS: { value: GuideTopic; label: string }[] = [
  { value: "automatic", label: "Automatic" },
  { value: "mint", label: "Mint a passport" },
  { value: "event", label: "Add an event" },
  { value: "trouble", label: "If it looks wrong" },
];

const monoMeta: React.CSSProperties = {
  fontFamily: "var(--f-mono)",
  fontSize: 10.5,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: "var(--ink-mute)",
};

function Section({
  heading,
  children,
}: {
  heading: string;
  children: React.ReactNode;
}) {
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={monoMeta}>{heading}</div>
      {children}
    </section>
  );
}

function Steps({ items }: { items: React.ReactNode[] }) {
  return (
    <ol
      style={{
        margin: 0,
        paddingLeft: 0,
        listStyle: "none",
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      {items.map((item, i) => (
        <li key={i} style={{ display: "flex", gap: 12 }}>
          <span
            style={{
              fontFamily: "var(--f-mono)",
              fontSize: 11,
              color: "var(--accent)",
              width: 18,
              flexShrink: 0,
              paddingTop: 2,
            }}
          >
            {String(i + 1).padStart(2, "0")}
          </span>
          <span style={{ fontSize: 13, lineHeight: 1.6, color: "var(--ink-soft)" }}>
            {item}
          </span>
        </li>
      ))}
    </ol>
  );
}

function Bullets({ items }: { items: React.ReactNode[] }) {
  return (
    <ul
      style={{
        margin: 0,
        paddingLeft: 18,
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      {items.map((item, i) => (
        <li
          key={i}
          style={{ fontSize: 13, lineHeight: 1.6, color: "var(--ink-soft)" }}
        >
          {item}
        </li>
      ))}
    </ul>
  );
}

function Note({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        borderLeft: "2px solid var(--accent)",
        padding: "8px 0 8px 12px",
        fontSize: 13,
        lineHeight: 1.6,
        color: "var(--ink-soft)",
      }}
    >
      {children}
    </div>
  );
}

function Strong({ children }: { children: React.ReactNode }) {
  return <b style={{ color: "var(--ink)", fontWeight: 500 }}>{children}</b>;
}

function MintGuide() {
  return (
    <>
      <Section heading="Where">
        <div style={{ fontSize: 13, lineHeight: 1.6, color: "var(--ink-soft)" }}>
          Passports → <Strong>+ Mint passport</Strong>. A passport is one watch,
          identified by brand, model and serial number.
        </div>
      </Section>

      <Section heading="Steps">
        <Steps
          items={[
            <>
              <Strong>Pick where the details come from.</Strong> Four sources,
              one per tab — they only change what gets pre-filled, the passport
              is the same either way.
            </>,
            <>
              <Strong>Search and select.</Strong> Type two or more characters;
              results appear as you type. Picking a Zoho bill with several line
              items asks you which item you are minting.
            </>,
            <>
              <Strong>Check the pre-filled details and complete the rest.</Strong>{" "}
              Fields marked with a red asterisk are required: brand, model,
              serial number, department and acquisition date. Everything else —
              reference, name, description, dial colour, case and bracelet
              material, gender, customer email — is optional. The passport{" "}
              <Strong>name is written for you</Strong> from brand, model,
              reference, dial, case material and gender, and keeps updating as
              those change; type over it and it stays exactly as you left it.
            </>,
            <>
              <Strong>Add photos.</Strong> Drop files or pick from the ticket's
              own attachments. Drag the tiles to reorder — the{" "}
              <Strong>first photo is the front image</Strong> shown on the
              passport. Hover a tile and press ✕ to remove it.
            </>,
            <>
              <Strong>Mint.</Strong> If something required is missing the form
              marks it instead of minting, and a line under the form says how
              many fields still need attention.
            </>,
          ]}
        />
      </Section>

      <Section heading="The four sources">
        <Bullets
          items={[
            <>
              <Strong>Desk ticket</Strong> — searches Zoho Desk. Brand, model,
              serial and reference are read from the ticket's custom fields, and
              the ticket's photos can be attached to the passport in one click.
            </>,
            <>
              <Strong>Zoho bill</Strong> — find the purchase bill, then the line
              item. Serial numbers already minted are marked so you cannot pick
              them twice.
            </>,
            <>
              <Strong>Zoho item</Strong> — search inventory by name or SKU and
              choose one of the item's serials.
            </>,
            <>
              <Strong>Manual</Strong> — for a watch that is not in Zoho yet.
              Type three or more characters of the{" "}
              <Strong>reference</Strong> and it is looked up in the watch
              catalogue: a single match fills brand, model, dial colour and case
              material on its own, and several matches open a list under the
              field to pick from. Punctuation is ignored, so{" "}
              <Strong>116610LN</Strong> and <Strong>116610-LN</Strong> find the
              same watch. Anything you typed yourself is never overwritten, and
              changing the reference re-runs the search.
            </>,
          ]}
        />
      </Section>

      <Section heading="What happens after you press Mint">
        <Steps
          items={[
            <>
              The photos are uploaded, then the mint is <Strong>queued</Strong>{" "}
              — the form confirms straight away, it does not wait for the
              blockchain.
            </>,
            <>
              The passport is created on Arianee in the background. This usually
              takes seconds but can take a couple of minutes.
            </>,
            <>
              It appears in Passports as <Strong>Pending</Strong>, then flips to{" "}
              <Strong>Verified</Strong> once the mint is confirmed. Reload the
              list if you do not see it yet.
            </>,
            <>
              An acquisition event — <Strong>Acquired by Phigora</Strong> or{" "}
              <Strong>Acquired by Watch Central</Strong>, following the
              department you chose — is added to the timeline, dated with the
              acquisition date.
            </>,
          ]}
        />
      </Section>

      <Note>
        A serial can only be minted once. If a passport already exists for that
        brand, model and serial, the mint is refused and the serial field says
        so.
      </Note>
    </>
  );
}

function EventGuide() {
  return (
    <>
      <Section heading="Where">
        <div style={{ fontSize: 13, lineHeight: 1.6, color: "var(--ink-soft)" }}>
          Passports → open a passport → <Strong>+ Add event</Strong>. Events are
          the passport's history: everything that happened to the watch after it
          was minted.
        </div>
      </Section>

      <Section heading="Steps">
        <Steps
          items={[
            <>
              <Strong>Choose a type</Strong> — service, auction, initial sale,
              warranty, resell, repair, refurbished, experience, transfer or
              delivery.
            </>,
            <>
              <Strong>Set the date</Strong> the event actually happened, not
              today's date.
            </>,
            <>
              <Strong>Write a title and description.</Strong> Both are shown to
              the customer on the passport, so keep them presentable.
            </>,
            <>
              <Strong>Attach photos and documents.</Strong> Documents may be
              PDFs — each page is turned into an image and stored with the
              event.
            </>,
            <>
              <Strong>Submit.</Strong> The event is written to the blockchain
              passport in the background and appears on the timeline a moment
              later.
            </>,
          ]}
        />
      </Section>

      <Note>
        Events cannot be edited or deleted once written — that is the point of a
        passport. Read it back before you submit.
      </Note>

      <Section heading="Related actions on the same page">
        <Bullets
          items={[
            <>
              The <Strong>pencil</Strong> next to the passport title edits the
              watch's own details and photos — not its history.
            </>,
            <>
              <Strong>Transfer</Strong> hands the passport to a customer. They
              have to accept it before ownership changes.
            </>,
          ]}
        />
      </Section>
    </>
  );
}

function AutomaticGuide() {
  return (
    <>
      <Section heading="What this covers">
        <div style={{ fontSize: 13, lineHeight: 1.6, color: "var(--ink-soft)" }}>
          Passports and events that appear on their own, from documents raised
          in Zoho Inventory. Nobody presses anything in this console — the three
          walkthroughs below are how you exercise it.
        </div>
      </Section>

      <Note>
        <Strong>Tick “Is test” or nothing will happen.</Strong> Every document
        below is ignored unless its <Strong>Is test</Strong> field is ticked. A
        document without it is skipped <Strong>silently</Strong> — no passport,
        no event, nothing in Issues. A ticked box, “yes”, “1” or “on” all count.
      </Note>

      <Section heading="1 · A bill creates the passports">
        <Steps
          items={[
            <>
              In Zoho Inventory raise a <Strong>bill</Strong> for a
              serial-tracked item and enter its serial numbers. A line with no
              serials has nothing to build a passport from.
            </>,
            <>
              Tick <Strong>Is test</Strong> and save it properly — a bill left
              as <Strong>draft or void</Strong> is ignored however it is
              flagged.
            </>,
            <>
              Expect one passport per serial in <Strong>Passports</Strong>{" "}
              within a moment, <Strong>with no photos</Strong> — that is by
              design, add them from the passport page.
            </>,
          ]}
        />
      </Section>

      <Section heading="2 · An invoice records the sale">
        <Steps
          items={[
            <>
              Raise an <Strong>invoice</Strong> to a customer for a serial you
              have already billed in, tick <Strong>Is test</Strong>, and save.
            </>,
            <>
              Expect a <Strong>Sold to …</Strong> event on that serial’s
              passport, naming the customer.
            </>,
            <>
              Invoice a serial that was never bought in and no passport is
              created — the mismatch is raised in <Strong>Issues</Strong>{" "}
              instead. That is the correct outcome, not a failure.
            </>,
          ]}
        />
      </Section>

      <Section heading="3 · A credit note brings it back">
        <Steps
          items={[
            <>
              Raise a <Strong>credit note</Strong> for the same customer and
              serial you just invoiced, tick <Strong>Is test</Strong>, and save.
            </>,
            <>
              Expect <Strong>Returned by …</Strong> on the timeline, because our
              own records show that customer bought that serial from us.
            </>,
            <>
              Do the same for a customer who never bought it and expect{" "}
              <Strong>Acquired by …</Strong> instead — that is a trade-in, and
              telling the two apart is the point of this test.
            </>,
          ]}
        />
      </Section>

      <Section heading="If nothing happened">
        <Bullets
          items={[
            <>
              <Strong>Is test</Strong> was not ticked — by far the most common
              cause.
            </>,
            <>The bill is still a draft, or was voided.</>,
            <>The line item carries no serial numbers.</>,
            <>
              It has been only a few seconds. The work runs in the background;
              reload the page before concluding anything.
            </>,
          ]}
        />
      </Section>
    </>
  );
}

function TroubleGuide() {
  return (
    <>
      <Section heading="Expected, not a bug">
        <Bullets
          items={[
            <>
              A new passport sits at <Strong>Pending</Strong> for a while — the
              mint is confirmed in the background and is retried for several
              minutes before it is given up on.
            </>,
            <>
              An event does not show the instant you submit it. Reload the
              passport after a moment.
            </>,
            <>
              A serial you expected to mint is greyed out — it already has a
              passport.
            </>,
          ]}
        />
      </Section>

      <Section heading="Worth reporting">
        <Bullets
          items={[
            <>Two passports for the same serial, or a doubled event.</>,
            <>
              A passport stuck at <Strong>Pending</Strong> or{" "}
              <Strong>Failed</Strong> long after minting.
            </>,
            <>
              Details that do not match the source ticket, bill or item — wrong
              brand, wrong serial, photos in the wrong order.
            </>,
            <>Anything that fails with an error you cannot get past.</>,
          ]}
        />
      </Section>

      <Section heading="How to report it">
        <Steps
          items={[
            <>
              Open <Strong>Send feedback</Strong> in the bar at the top. Use it
              for anything broken, anything missing, and questions too — pick
              the type at the top of the form.
            </>,
            <>
              Say what you did and what you expected instead. Name the{" "}
              <Strong>serial number</Strong> or the record you were working on —
              without it a report usually cannot be chased.
            </>,
            <>
              <Strong>Attach a screenshot.</Strong> Up to five, on any type of
              report; one picture of the screen normally settles what a
              paragraph cannot.
            </>,
          ]}
        />
      </Section>

      <Note>
        Your email, the page you were on and your browser are attached for you —
        no need to describe them. Nothing you send is lost if it fails: the form
        keeps what you typed so you can send it again.
      </Note>
    </>
  );
}

const GUIDES: Record<GuideTopic, () => React.ReactElement> = {
  mint: MintGuide,
  event: EventGuide,
  automatic: AutomaticGuide,
  trouble: TroubleGuide,
};

interface GuideModalProps {
  open: boolean;
  onClose: () => void;
  topic?: GuideTopic;
}

export function GuideModal({
  open,
  onClose,
  topic = "automatic",
}: GuideModalProps) {
  const [active, setActive] = useState<GuideTopic>(topic);
  const Guide = GUIDES[active];

  useEffect(() => {
    if (open) setActive(topic);
  }, [open, topic]);

  return (
    <Modal
      open={open}
      title="How passports work"
      onClose={onClose}
      maxWidth={720}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        <div
          style={{
            display: "flex",
            gap: 4,
            flexWrap: "wrap",
            borderBottom: "1px solid var(--line)",
          }}
        >
          {TOPICS.map((t) => (
            <button
              key={t.value}
              type="button"
              onClick={() => setActive(t.value)}
              style={{
                background: "transparent",
                border: 0,
                borderBottom: `2px solid ${
                  active === t.value ? "var(--accent)" : "transparent"
                }`,
                color: active === t.value ? "var(--ink)" : "var(--ink-mute)",
                fontFamily: "var(--f-mono)",
                fontSize: 10.5,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                cursor: "pointer",
                padding: "8px 12px",
                marginBottom: -1,
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          <Guide />
        </div>
      </div>
    </Modal>
  );
}
